import { Box, Stack, StackDivider, VStack } from "@chakra-ui/react";
import {
  ForwardedRef,
  Ref,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import ReactPlayer from "react-player";
import { OnProgressProps } from "react-player/base";
import useHomeworkState from "../state/homework.state";
import { BaseAudio } from "../utils/audio";
import { getElapsedFromStart, sortByStartTime } from "../utils/common";
import AudioForm from "./forms/AudioForm";
import AudioItem from "./items/AudioItem";

export type AudioManagerHandle = {
  onPlay: () => void;
  onPause: () => void;
  onSeek: () => void;
  onProgress: (
    videoProgress: OnProgressProps,
    volumeOnAudio?: number,
  ) => Promise<void>;
};

type Props = {
  videoRef: ReactPlayer;
  audios: BaseAudio[];
  setAudios: (audioDescriptions: BaseAudio[]) => void;
  isDisabled?: boolean;
};

const AudioManager = (
  { videoRef, audios, setAudios, isDisabled = false }: Props,
  ref: ForwardedRef<AudioManagerHandle>,
) => {
  const { t } = useTranslation();
  const { categories } = useHomeworkState();
  const audioToPlay = useRef<HTMLAudioElement | null>();
  const audioObjectURL = useRef<string | null>();
  const [audioIndexToEdit, setAudioIndexToEdit] = useState<number | null>(null);

  useImperativeHandle(
    ref,
    () => ({
      onPlay: () => {
        if (audioToPlay.current) {
          audioToPlay.current.play().catch(console.warn);
        }
      },
      onPause: () => {
        if (audioToPlay.current) {
          audioToPlay.current.pause();
        }
      },
      onSeek: () => {
        if (audioToPlay.current) {
          audioToPlay.current.pause();
          audioToPlay.current = null;
          const videoElement = videoRef.getInternalPlayer();
          if (videoElement instanceof HTMLVideoElement) {
            videoElement.volume = 1;
          }
        }
      },
      onProgress: async (videoProgress: OnProgressProps, volumeOnAudio = 0) => {
        const videoElement = videoRef.getInternalPlayer();
        if (
          videoElement instanceof HTMLVideoElement &&
          !audioToPlay.current &&
          !videoElement.paused
        ) {
          if (audios.length) {
            let audioElapsed = 0;
            let videoElapsed = videoProgress?.playedSeconds ?? -1;
            const _audioToPlay = audios.find((audio) => {
              audioElapsed = getElapsedFromStart(audio.startTime);
              videoElapsed = videoProgress?.playedSeconds ?? -1;

              return (
                videoElapsed >= audioElapsed &&
                // If there are only 500ms or less to reproduce, skip the audio on seek
                videoElapsed <
                  Math.trunc(audioElapsed + audio.duration / 1000) - 0.5
              );
            });
            if (_audioToPlay) {
              videoElement.volume = volumeOnAudio;
              const audioBlob = await (await fetch(_audioToPlay.base64)).blob();
              audioObjectURL.current = URL.createObjectURL(audioBlob);
              audioToPlay.current = new Audio(audioObjectURL.current);
              audioToPlay.current.play().catch(console.warn);
              audioToPlay.current.currentTime = videoElapsed - audioElapsed;
              audioToPlay.current.onended = () => {
                videoElement.volume = 1;
                URL.revokeObjectURL(audioObjectURL.current as string);
                audioObjectURL.current = null;
                audioToPlay.current = null;
              };
            }
          }
        }
      },
    }),
    [videoRef, audios],
  );

  const onSubmit = useCallback(
    (audio: BaseAudio) => {
      let newAudios: BaseAudio[] = [];

      if (audioIndexToEdit === null) {
        newAudios = [...audios, audio];
      } else {
        newAudios = audios.map((oldAudio, index) =>
          index === audioIndexToEdit ? audio : oldAudio,
        );
      }

      setAudios(newAudios.sort(sortByStartTime));
      setAudioIndexToEdit(null);
    },
    [audioIndexToEdit, audios, setAudios],
  );

  const onAudioClick = useCallback(
    (audio: BaseAudio) => {
      videoRef?.seekTo(getElapsedFromStart(audio.startTime));
    },
    [videoRef],
  );

  const onAudioDelete = useCallback(
    (audioIndex: number) => {
      setAudios(audios.filter((_, index) => index !== audioIndex));
      setAudioIndexToEdit(null);
    },
    [audios, setAudios],
  );

  useEffect(() => {
    return () => {
      if (audioObjectURL.current) URL.revokeObjectURL(audioObjectURL.current);
    };
  }, []);

  return (
    <Stack divider={<StackDivider borderColor="gray.200" />} gap={4}>
      {!isDisabled && (
        <Box>
          <AudioForm
            defaultValue={
              audioIndexToEdit !== null ? audios[audioIndexToEdit] : undefined
            }
            onSubmit={onSubmit}
            buttonText={
              audioIndexToEdit !== null
                ? `${t("common.buttons.edit")} #${audioIndexToEdit}`
                : t("common.buttons.add")
            }
            isDisabled={isDisabled}
          />
        </Box>
      )}
      <VStack align="stretch" divider={<StackDivider borderColor="gray.200" />}>
        {audios.map((audio, index) => (
          <AudioItem
            key={`${audio.startTime}${audio.duration}${index}`}
            audio={audio}
            category={
              audio.categoryUuid ? categories[audio.categoryUuid] : undefined
            }
            index={index}
            isSelected={audioIndexToEdit === index}
            isDisabled={isDisabled}
            onClick={onAudioClick}
            onDelete={onAudioDelete}
            onEdit={setAudioIndexToEdit}
            onLeaveEdit={setAudioIndexToEdit}
          />
        ))}
      </VStack>
    </Stack>
  );
};

export default forwardRef(AudioManager) as (
  props: Props & { ref?: Ref<AudioManagerHandle> },
) => JSX.Element;
